import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRole } from '@/contexts/RoleContext';
import { 
  Bell, X, Check, CheckCheck, Trash2, MessageSquare, FileText, 
  FolderOpen, Clock, Calculator, Layers, Eye, AlertCircle, Settings,
  ClipboardList, Save, CheckCircle2, UserPlus, Users, ChevronDown, ChevronRight,
  Filter, SlidersHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { getCurrentUserId } from '../utils/currentUser';
import { cacheBranding } from '../utils/skin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
// Determine WebSocket protocol based on backend URL or page protocol
const getWsProtocol = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (!backendUrl) return window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return backendUrl.startsWith('https') ? 'wss:' : 'ws:';
};

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  // New notification types from notification system
  client_selection_approval: {
    icon: CheckCircle2,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  client_selection_rejection: {
    icon: AlertCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30'
  },
  new_memo_added: {
    icon: MessageSquare,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  },
  site_visit_logged: {
    icon: ClipboardList,
    color: 'text-teal-500',
    bgColor: 'bg-teal-100 dark:bg-teal-900/30'
  },
  new_meeting_note: {
    icon: Users,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30'
  },
  document_signed: { 
    icon: FileText, 
    color: 'text-green-500', 
    bgColor: 'bg-green-100 dark:bg-green-900/30' 
  },
  phase_changed: {
    icon: FolderOpen,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30'
  },
  // Existing notification types
  message: { 
    icon: MessageSquare, 
    color: 'text-blue-500', 
    bgColor: 'bg-blue-100 dark:bg-blue-900/30' 
  },
  message_received: { 
    icon: MessageSquare, 
    color: 'text-blue-500', 
    bgColor: 'bg-blue-100 dark:bg-blue-900/30' 
  },
  document_awaiting_signature: { 
    icon: FileText, 
    color: 'text-orange-500', 
    bgColor: 'bg-orange-100 dark:bg-orange-900/30' 
  },
  quiet_hours_digest: {
    icon: Bell,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30'
  },
  project_update: { 
    icon: FolderOpen, 
    color: 'text-purple-500', 
    bgColor: 'bg-purple-100 dark:bg-purple-900/30' 
  },
  project_status_changed: { 
    icon: FolderOpen, 
    color: 'text-purple-500', 
    bgColor: 'bg-purple-100 dark:bg-purple-900/30' 
  },
  task_due: { 
    icon: Clock, 
    color: 'text-orange-500', 
    bgColor: 'bg-orange-100 dark:bg-orange-900/30' 
  },
  task_due_soon: { 
    icon: Clock, 
    color: 'text-orange-500', 
    bgColor: 'bg-orange-100 dark:bg-orange-900/30' 
  },
  task_overdue: { 
    icon: AlertCircle, 
    color: 'text-red-500', 
    bgColor: 'bg-red-100 dark:bg-red-900/30' 
  },
  task_assigned: { 
    icon: UserPlus, 
    color: 'text-blue-500', 
    bgColor: 'bg-blue-100 dark:bg-blue-900/30' 
  },
  estimate_created: { 
    icon: Calculator, 
    color: 'text-emerald-500', 
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' 
  },
  clip_added: { 
    icon: Layers, 
    color: 'text-pink-500', 
    bgColor: 'bg-pink-100 dark:bg-pink-900/30' 
  },
  site_tag_done: {
    icon: CheckCircle2,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30'
  },
  site_tag_sent_back: {
    icon: AlertCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30'
  },
  portal_accessed: { 
    icon: Eye, 
    color: 'text-cyan-500', 
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' 
  },
  survey_started: { 
    icon: ClipboardList, 
    color: 'text-amber-500', 
    bgColor: 'bg-amber-100 dark:bg-amber-900/30' 
  },
  survey_saved: { 
    icon: Save, 
    color: 'text-blue-400', 
    bgColor: 'bg-blue-50 dark:bg-blue-900/20' 
  },
  survey_completed: { 
    icon: CheckCircle2, 
    color: 'text-emerald-600', 
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' 
  },
  survey_response_received: { 
    icon: ClipboardList, 
    color: 'text-emerald-500', 
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' 
  },
  payment_received: { 
    icon: Calculator, 
    color: 'text-green-500', 
    bgColor: 'bg-green-100 dark:bg-green-900/30' 
  },
  new_activity: { 
    icon: Users, 
    color: 'text-purple-500', 
    bgColor: 'bg-purple-100 dark:bg-purple-900/30' 
  },
  contractor_document_expiring: { 
    icon: AlertCircle, 
    color: 'text-yellow-500', 
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' 
  },
  system: { 
    icon: Settings, 
    color: 'text-gray-500', 
    bgColor: 'bg-gray-100 dark:bg-gray-800' 
  }
};

// Notification sound - using Web Audio API for a pleasant chime
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a pleasant two-tone chime
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      // Gentle fade in/out
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    playTone(880, now, 0.15);        // A5
    playTone(1108.73, now + 0.1, 0.2); // C#6 - pleasant major third
  } catch (e) {
    console.log('Could not play notification sound:', e);
  }
};

// Update browser tab title with notification count
const updateTabTitle = (count) => {
  const baseTitle = 'KEY-MADE';
  if (count > 0) {
    document.title = `(${count}) ${baseTitle}`;
  } else {
    document.title = baseTitle;
  }
};

function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

// Group notifications by project and type for cleaner display
function groupNotifications(notifications) {
  if (!notifications || notifications.length === 0) return [];
  
  const groups = {};
  const standalone = [];
  
  // Only group unread notifications from the same project+type within last hour
  const oneHourAgo = Date.now() - (60 * 60 * 1000);
  
  notifications.forEach(notification => {
    if (!notification) return;
    
    const notifType = notification.notification_type || notification.type;
    const projectId = notification.project_id;
    const createdAt = notification.created_at;
    
    if (!createdAt) {
      standalone.push(notification);
      return;
    }
    
    const isRecent = new Date(createdAt).getTime() > oneHourAgo;
    const isUnread = !notification.is_read && !notification.read;
    
    // Only group if: has project_id, is recent, is unread, and is a groupable type
    const groupableTypes = ['new_memo_added', 'site_visit_logged', 'new_meeting_note', 'client_selection_approval', 'client_selection_rejection'];
    
    if (projectId && isRecent && isUnread && groupableTypes.includes(notifType)) {
      const groupKey = `${projectId}_${notifType}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          type: notifType,
          project_id: projectId,
          project_name: notification.project_name,
          notifications: [],
          latest: notification
        };
      }
      groups[groupKey].notifications.push(notification);
      // Keep the latest one as the representative
      if (new Date(createdAt) > new Date(groups[groupKey].latest.created_at)) {
        groups[groupKey].latest = notification;
      }
    } else {
      standalone.push(notification);
    }
  });
  
  // Convert groups to array and combine with standalone
  const groupedItems = Object.values(groups).map(group => {
    if (group.notifications.length === 1) {
      // Single notification - don't group
      return { type: 'single', notification: group.notifications[0] };
    }
    return { type: 'group', ...group };
  });
  
  const standaloneItems = standalone.map(n => ({ type: 'single', notification: n }));
  
  // Combine and sort by date
  const allItems = [...groupedItems, ...standaloneItems].sort((a, b) => {
    const getDate = (item) => {
      if (item.type === 'group' && item.latest?.created_at) {
        return new Date(item.latest.created_at);
      }
      if (item.notification?.created_at) {
        return new Date(item.notification.created_at);
      }
      return new Date(0);
    };
    return getDate(b) - getDate(a);
  });
  
  return allItems;
}

// Get group title based on type
function getGroupTitle(type, count, projectName) {
  const titles = {
    new_memo_added: `${count} new memos`,
    site_visit_logged: `${count} site visits logged`,
    new_meeting_note: `${count} meeting notes`,
    client_selection_approval: `${count} selections approved`,
    client_selection_rejection: `${count} selections rejected`
  };
  const baseTitle = titles[type] || `${count} notifications`;
  return projectName ? `${baseTitle} on ${projectName}` : baseTitle;
}

export default function NotificationsPanel({ 
  darkMode = false,
  userRole = null,
  userId = getCurrentUserId(),
  prominent = false  // More prominent style for project pages
}) {
  const navigate = useNavigate();
  const { role: contextRole } = useRole();
  const effectiveRole = userRole || contextRole;
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [filterType, setFilterType] = useState('all'); // 'all', 'memos', 'site_visits', 'client_actions', 'projects'
  const [filterProject, setFilterProject] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const wsRef = useRef(null);

  // Get unique projects from notifications for filter dropdown
  const uniqueProjects = [...new Set(notifications.filter(n => n?.project_name).map(n => n.project_name))];

  // Filter notifications based on selected filters
  const filteredNotifications = notifications.filter(n => {
    if (!n) return false;
    
    const type = n.notification_type || n.type;
    
    // Type filter
    if (filterType !== 'all') {
      const typeMap = {
        'memos': ['new_memo_added'],
        'site_visits': ['site_visit_logged'],
        'meetings': ['new_meeting_note'],
        'client_actions': ['client_selection_approval', 'client_selection_rejection', 'document_signed'],
        'projects': ['project_created', 'project_update', 'project_status_changed', 'phase_changed']
      };
      if (!typeMap[filterType]?.includes(type)) return false;
    }
    
    // Project filter
    if (filterProject !== 'all' && n.project_name !== filterProject) return false;
    
    return true;
  });

  const toggleGroupExpanded = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Fetch from both old notifications endpoint and new user-specific notifications
      const params = new URLSearchParams({ user_role: effectiveRole });
      if (userId) params.append('user_id', userId);
      
      // Parallel fetch from both endpoints
      const [oldResponse, newResponse] = await Promise.all([
        axios.get(`${API}/notifications?${params}`).catch(() => ({ data: { notifications: [], unread_count: 0 } })),
        axios.get(`${API}/users/${userId}/notifications?limit=50`).catch(() => ({ data: { notifications: [], unread_count: 0 } }))
      ]);
      
      // Merge notifications from both sources
      const oldNotifications = (oldResponse.data.notifications || []).map(n => ({
        ...n,
        _source: 'global' // Mark source for proper deletion
      }));
      const newNotifications = (newResponse.data.notifications || []).map(n => ({
        ...n,
        notification_type: n.type,
        message: n.body,
        link: n.project_id ? `/projects/${n.project_id}/hub?tab=activity` : null,
        _source: 'user' // Mark source for proper deletion
      }));
      
      // Combine and deduplicate by ID (prefer user notifications)
      const seenIds = new Set();
      const deduped = [];
      
      // Add user notifications first (they take priority)
      for (const n of newNotifications) {
        if (n.id && !seenIds.has(n.id)) {
          seenIds.add(n.id);
          deduped.push(n);
        }
      }
      // Then add global notifications if not already seen
      for (const n of oldNotifications) {
        if (n.id && !seenIds.has(n.id)) {
          seenIds.add(n.id);
          deduped.push(n);
        }
      }
      
      // Sort by date
      const combined = deduped.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setNotifications(combined.slice(0, 50));
      
      // Calculate unread count from the deduplicated list
      const unreadFromList = combined.filter(n => !n.is_read && !n.read).length;
      setUnreadCount(unreadFromList);
      updateTabTitle(unreadFromList);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      // DP92 Part D — branding arrives on THIS socket, the one that already
      // carries notifications and presence. No second socket, no reload, no
      // state thrown away: cacheBranding writes the cache and re-applies the
      // tokens on <body>. A user with dark mode on stays on night, because
      // resolveSkin puts dark mode ahead of the firm's skin.
      case 'branding_changed':
        cacheBranding(data.branding || {});
        break;
      case 'init':
        setUnreadCount(data.unread_count || 0);
        updateTabTitle(data.unread_count || 0);
        break;
      
      case 'notification':
        // New notification received
        const newNotification = data.notification;
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => {
          const newCount = prev + 1;
          updateTabTitle(newCount);
          return newCount;
        });
        
        // Play notification sound
        playNotificationSound();
        
        // Show toast for new notification
        toast(newNotification.title, {
          description: newNotification.message,
          action: newNotification.link ? {
            label: 'View',
            onClick: () => navigate(newNotification.link)
          } : undefined
        });
        break;
      
      case 'unread_count':
        setUnreadCount(data.count || 0);
        updateTabTitle(data.count || 0);
        break;
      
      default:
        break;
    }
  }, [navigate]);

  // WebSocket connection
  useEffect(() => {
    const wsProtocol = getWsProtocol();
    // Round 4 Phase 1c: JWT required as query param. user_id is now derived
    // server-side from the JWT and the query value is ignored.
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    if (!token) return; // no point trying to connect without a token
    const wsUrl = `${wsProtocol}//${WS_BASE}/ws/notifications?token=${encodeURIComponent(token)}`;
    
    const connectWebSocket = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        setIsConnected(true);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };
      
      ws.onclose = () => {
        setIsConnected(false);
        // Reconnect after delay
        setTimeout(connectWebSocket, 5000);
      };
      
      ws.onerror = () => {
        setIsConnected(false);
      };
    };
    
    connectWebSocket();
    
    // Heartbeat to keep connection alive
    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => {
      clearInterval(heartbeatInterval);
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [userId, handleWebSocketMessage]);

  // Initial fetch and polling fallback
  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 60 seconds (reduced frequency, fallback when WebSocket unavailable)
    const interval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        fetchNotifications();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [effectiveRole, userId]); // Removed isConnected from dependencies

  const markAsRead = async (notificationId) => {
    try {
      // Find the notification
      const notification = notifications.find(n => n.id === notificationId);
      
      // Skip if already read
      if (notification && (notification.is_read || notification.read)) {
        return;
      }
      
      // Try via WebSocket first
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_read', notification_id: notificationId }));
      }
      
      // Mark as read in both systems to ensure consistency
      const userReadPromise = userId 
        ? axios.put(`${API}/users/${userId}/notifications/${notificationId}/read`).catch(() => null)
        : Promise.resolve(null);
      
      const globalReadPromise = axios.put(`${API}/notifications/${notificationId}/read`).catch(() => null);
      
      await Promise.all([userReadPromise, globalReadPromise]);
      
      // Update local state immediately
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read: true } : n)
      );
      setUnreadCount(prev => {
        const newCount = Math.max(0, prev - 1);
        updateTabTitle(newCount);
        return newCount;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Mark all in both systems
      await Promise.all([
        axios.put(`${API}/notifications/read-all`).catch(() => {}),
        userId ? axios.put(`${API}/users/${userId}/notifications/mark-all-read`).catch(() => {}) : Promise.resolve()
      ]);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
      updateTabTitle(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      // Find the notification to check source
      const notification = notifications.find(n => n.id === notificationId);
      
      // Delete from both endpoints to ensure it's gone
      // User notifications endpoint
      const userDeletePromise = userId 
        ? axios.delete(`${API}/users/${userId}/notifications/${notificationId}`).catch(() => null)
        : Promise.resolve(null);
      
      // Global notifications endpoint  
      const globalDeletePromise = axios.delete(`${API}/notifications/${notificationId}`).catch(() => null);
      
      // Wait for both delete attempts
      await Promise.all([userDeletePromise, globalDeletePromise]);
      
      // Remove from local state immediately
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Update unread count if the deleted notification was unread
      if (notification && !notification.is_read && !notification.read) {
        setUnreadCount(prev => {
          const newCount = Math.max(0, prev - 1);
          updateTabTitle(newCount);
          return newCount;
        });
      }
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read && !notification.read) {
      markAsRead(notification.id);
    }
    
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  // Helper to check if notification is read (handles both field names)
  const isNotificationRead = (notification) => notification.is_read || notification.read;

  const config = (type) => NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.system;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant={prominent ? "default" : "ghost"} 
          size="icon" 
          className={`relative ${
            prominent 
              ? 'bg-[#BC5D41] hover:bg-[#BC5D41]/90 text-white'
              : (darkMode ? 'text-[#E5E2DD] hover:bg-[#1A1A1A]' : '')
          }`}
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full ${
              prominent ? 'bg-white text-[#BC5D41]' : 'bg-[#BC5D41] text-white'
            }`}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={`w-96 p-0 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
        align="end"
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>Notifications</h3>
            {unreadCount > 0 && (
              <Badge className="bg-[#BC5D41] text-white text-xs">{unreadCount} new</Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className={`text-xs h-7 ${darkMode ? 'text-[#BC5D41] hover:bg-[#1A1A1A]' : 'text-[#70170F]'}`}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Filter Bar */}
        <div className={`px-3 py-2 border-b flex items-center gap-2 ${darkMode ? 'border-[#2A2A2A] bg-[#0A0A0A]' : 'bg-gray-50 border-gray-100'}`}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
              showFilters || filterType !== 'all' || filterProject !== 'all'
                ? 'bg-[#BC5D41] text-white' 
                : darkMode ? 'bg-[#1A1A1A] text-gray-400 hover:text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Filter className="w-3 h-3" />
            Filters
            {(filterType !== 'all' || filterProject !== 'all') && (
              <span className="ml-1 bg-white/20 px-1 rounded text-[10px]">
                {(filterType !== 'all' ? 1 : 0) + (filterProject !== 'all' ? 1 : 0)}
              </span>
            )}
          </button>
          
          {/* Quick filter pills */}
          <div className="flex items-center gap-1 flex-1 overflow-x-auto">
            {['all', 'memos', 'site_visits', 'client_actions'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors ${
                  filterType === type
                    ? 'bg-[#BC5D41] text-white'
                    : darkMode ? 'bg-[#1A1A1A] text-gray-400 hover:text-white' : 'bg-white text-gray-500 hover:bg-gray-100'
                }`}
              >
                {type === 'all' ? 'All' : type === 'memos' ? 'Memos' : type === 'site_visits' ? 'Site Visits' : 'Client'}
              </button>
            ))}
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className={`px-3 py-2 border-b ${darkMode ? 'border-[#2A2A2A] bg-[#0A0A0A]' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center gap-2">
              <label className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Project:</label>
              <select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                className={`flex-1 text-xs px-2 py-1 rounded border ${
                  darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : 'bg-white border-gray-200'
                }`}
              >
                <option value="all">All Projects</option>
                {uniqueProjects.map(proj => (
                  <option key={proj} value={proj}>{proj}</option>
                ))}
              </select>
              {(filterType !== 'all' || filterProject !== 'all') && (
                <button
                  onClick={() => { setFilterType('all'); setFilterProject('all'); }}
                  className="text-xs text-[#BC5D41] hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="max-h-[350px] overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className={`p-8 text-center ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-500'}`}>
              <div className="animate-pulse">Loading...</div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className={`p-8 text-center ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-500'}`}>
              <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{notifications.length > 0 ? 'No matching notifications' : 'No notifications yet'}</p>
              <p className="text-xs mt-1">{notifications.length > 0 ? 'Try adjusting your filters' : "We'll notify you when something happens"}</p>
              {notifications.length > 0 && (filterType !== 'all' || filterProject !== 'all') && (
                <button
                  onClick={() => { setFilterType('all'); setFilterProject('all'); }}
                  className="mt-2 text-xs text-[#BC5D41] hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            (() => {
              // Group consecutive notifications of the same type and project
              const grouped = [];
              let currentGroup = null;
              
              const shouldGroup = (n) => {
                const type = n.notification_type || n.type;
                return ['new_memo_added', 'site_visit_logged', 'new_meeting_note'].includes(type) 
                       && !n.is_read && !n.read 
                       && n.project_id;
              };
              
              filteredNotifications.forEach((notif, idx) => {
                if (!notif) return;
                
                const type = notif.notification_type || notif.type;
                const groupKey = `${notif.project_id}_${type}`;
                
                if (shouldGroup(notif)) {
                  if (currentGroup && currentGroup.key === groupKey) {
                    // Add to existing group
                    currentGroup.items.push(notif);
                  } else {
                    // Start new group
                    if (currentGroup) grouped.push(currentGroup);
                    currentGroup = { type: 'group', key: groupKey, notifType: type, projectName: notif.project_name, items: [notif] };
                  }
                } else {
                  // Not groupable - push any pending group and add as single
                  if (currentGroup) {
                    grouped.push(currentGroup);
                    currentGroup = null;
                  }
                  grouped.push({ type: 'single', notification: notif });
                }
              });
              if (currentGroup) grouped.push(currentGroup);
              
              return grouped.map((item, idx) => {
                if (item.type === 'group' && item.items.length > 1) {
                  // Render grouped
                  const { icon: Icon, color, bgColor } = config(item.notifType);
                  const isExpanded = expandedGroups.has(item.key);
                  const titles = { new_memo_added: 'new memos', site_visit_logged: 'site visits', new_meeting_note: 'meeting notes' };
                  const title = `${item.items.length} ${titles[item.notifType] || 'notifications'}${item.projectName ? ` on ${item.projectName}` : ''}`;
                  
                  return (
                    <div key={item.key}>
                      <div
                        className={`p-3 border-b cursor-pointer transition-colors bg-[#BC5D41]/5 border-gray-100`}
                        onClick={() => toggleGroupExpanded(item.key)}
                      >
                        <div className="flex gap-3">
                          <div className={`p-2 rounded-lg ${bgColor}`}>
                            <Icon className={`w-4 h-4 ${color}`} strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-gray-900">{title}</p>
                                <Badge className="bg-[#BC5D41] text-white text-[10px] px-1.5 py-0">{item.items.length}</Badge>
                              </div>
                              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{formatTimeAgo(item.items[0].created_at)}</p>
                          </div>
                        </div>
                      </div>
                      {isExpanded && item.items.map((n) => (
                        <div key={n.id} className="p-3 pl-12 border-b border-gray-100 bg-gray-50 cursor-pointer" onClick={() => handleNotificationClick(n)}>
                          <p className="text-xs text-gray-600">{n.message || n.body}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{formatTimeAgo(n.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  );
                }
                
                // Single notification (or group of 1)
                const notification = item.type === 'single' ? item.notification : item.items[0];
                if (!notification) return null;
                
                const { icon: Icon, color, bgColor } = config(notification.notification_type || notification.type);
                const isRead = isNotificationRead(notification);
                
                return (
                  <div
                    key={notification.id || idx}
                    className={`p-3 border-b cursor-pointer transition-colors border-gray-100 ${!isRead ? 'bg-[#BC5D41]/5' : 'hover:bg-gray-50'}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      <div className={`p-2 rounded-lg ${bgColor}`}>
                        <Icon className={`w-4 h-4 ${color}`} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium text-gray-900 ${!isRead ? 'font-semibold' : ''}`}>{notification.title}</p>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!isRead && (
                              <button onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }} className="p-1 rounded hover:bg-gray-200 text-gray-400" title="Mark as read">
                                <Check className="w-3 h-3" />
                              </button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }} className="p-1 rounded hover:bg-red-100 text-red-500" title="Delete">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs mt-0.5 line-clamp-2 text-gray-600">{notification.message || notification.body}</p>
                        <p className="text-xs mt-1 text-gray-400">{formatTimeAgo(notification.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              });
            })()
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className={`p-2 border-t ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
            <Button 
              variant="ghost" 
              size="sm" 
              className={`w-full text-xs ${darkMode ? 'text-[#8A8A8A] hover:bg-[#1A1A1A]' : ''}`}
              onClick={() => { setOpen(false); navigate('/notifications'); }}
            >
              View all notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
